import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { busesService } from '../../services';
import { CreateBusRequest } from '../../types';

// Form validation schema
const busSchema = z.object({
  plateNumber: z
    .string()
    .min(1, 'Plate number is required')
    .regex(/^[0-9A-Z\-]+$/, 'Invalid plate number format'),
  label: z.string().min(1, 'Label is required').max(100, 'Label must be less than 100 characters'),
});

type BusFormData = z.infer<typeof busSchema>;

export const BusForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BusFormData>({
    resolver: zodResolver(busSchema),
  });

  // Load bus if editing
  useEffect(() => {
    if (id) {
      const loadBus = async () => {
        try {
          const bus = await busesService.getBus(id);
          reset({
            plateNumber: bus.plateNumber,
            label: bus.label,
          });
        } catch (err) {
          setError('Failed to load bus');
        }
      };
      loadBus();
    }
  }, [id, reset]);

  const onSubmit = async (data: BusFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const request: CreateBusRequest = {
        plateNumber: data.plateNumber,
        label: data.label,
      };

      if (id) {
        await busesService.updateBus(id, request);
      } else {
        await busesService.createBus(request);
      }

      navigate('/buses');
    } catch (err: any) {
      setError(err.message || 'Failed to save bus');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{id ? 'Edit Bus' : 'Add Bus'}</h1>
        <p className="text-gray-600 mt-1">
          {id ? 'Update bus details' : 'Register a new bus in your fleet'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-8 max-w-2xl">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Plate Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plate Number</label>
            <input
              type="text"
              placeholder="e.g., 1-ABC-123"
              {...register('plateNumber')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.plateNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.plateNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.plateNumber.message}</p>
            )}
            <p className="text-gray-500 text-sm mt-2">Belgian format: 1-ABC-123</p>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
            <input
              type="text"
              placeholder="e.g., Bus 001, Main Fleet"
              {...register('label')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
                errors.label ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {errors.label && <p className="text-red-500 text-sm mt-1">{errors.label.message}</p>}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-4 mt-8">
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition font-medium"
          >
            {isLoading ? 'Saving...' : 'Save Bus'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/buses')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
